/**
 * BY LEAGUE mega menu: switch team panels when hovering/focusing a league in the sidebar.
 */
class ByLeagueMenu extends HTMLElement {
  /** @type {HTMLElement[]} */
  #triggers = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  connectedCallback() {
    this.#triggers = Array.from(this.querySelectorAll('[data-by-league-trigger]'));
    this.#panels = Array.from(this.querySelectorAll('[data-by-league-panel]'));

    if (!this.#triggers.length || !this.#panels.length) return;

    this.#triggers.forEach((trigger) => {
      trigger.addEventListener('pointerenter', this.#onActivate);
      trigger.addEventListener('focus', this.#onActivate);
      trigger.addEventListener('keydown', this.#onKeyDown);
    });
  }

  disconnectedCallback() {
    this.#triggers.forEach((trigger) => {
      trigger.removeEventListener('pointerenter', this.#onActivate);
      trigger.removeEventListener('focus', this.#onActivate);
      trigger.removeEventListener('keydown', this.#onKeyDown);
    });
  }

  /**
   * @param {Event} event
   */
  #onActivate = (event) => {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;
    this.#activate(trigger);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;

    const index = this.#triggers.indexOf(trigger);
    if (index < 0) return;

    let nextIndex = index;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      nextIndex = (index + 1) % this.#triggers.length;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextIndex = (index - 1 + this.#triggers.length) % this.#triggers.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this.#triggers.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    const next = this.#triggers[nextIndex];
    this.#activate(next);
    next.focus();
  };

  /**
   * @param {HTMLElement} trigger
   */
  #activate(trigger) {
    const panelId = trigger.getAttribute('data-panel');
    if (!panelId) return;

    this.#triggers.forEach((item) => {
      const active = item === trigger;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-selected', active ? 'true' : 'false');
      item.tabIndex = active ? 0 : -1;
    });

    this.#panels.forEach((panel) => {
      const active = panel.id === panelId;
      panel.classList.toggle('is-active', active);
      if (active) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#syncSubmenuHeight());
    });
  }

  #syncSubmenuHeight() {
    const submenu = this.closest('[ref="submenu[]"]');
    const header = /** @type {HTMLElement | null} */ (this.closest('header-component'));
    if (!(submenu instanceof HTMLElement) || !header) return;

    // Only resize the header underlay while this submenu is open.
    const listItem = submenu.closest('.menu-list__list-item');
    const trigger = listItem?.querySelector('.menu-list__link--has-submenu');
    const isOpen =
      submenu.hasAttribute('data-active') ||
      trigger?.getAttribute('aria-expanded') === 'true';
    if (!isOpen) return;

    const inner = submenu.querySelector('.menu-list__submenu-inner');
    const measured = Math.max(
      submenu.scrollHeight,
      submenu.offsetHeight,
      inner instanceof HTMLElement ? inner.scrollHeight : 0,
      this.scrollHeight
    );

    header.style.setProperty('--submenu-height', `${measured}px`);

    const topRow = header.querySelector('.header__row--top');
    const headerVisible =
      (topRow instanceof HTMLElement ? topRow.getBoundingClientRect().height : 0) ||
      header.getBoundingClientRect().height ||
      0;

    header.style.setProperty('--full-open-header-height', `${measured + headerVisible}px`);
  }
}

if (!customElements.get('by-league-menu')) {
  customElements.define('by-league-menu', ByLeagueMenu);
}
